import { z } from 'zod'

import { EnumBuilder } from '@mntn-dev/utility-types'
import { ModelFilterSchema } from '../model-filter-schema.ts'
import {
  DescriptionSchema,
  DollarsSchema,
  NameSchema,
  TranslationsSchema,
} from './property.models.ts'
import { PackageIdSchema, PackageUrnSchema } from './unique-id.models.ts'

/**
 * PackageSource
 */
export const [PackageSources, PackageSourceSchema, PackageSourceEnum] =
  EnumBuilder('ctv', 'tiktok', 'meta', 'quickframe')
export type PackageSource = z.infer<typeof PackageSourceSchema>

/**
 * PackageTranslations
 */
export const PackageTranslationsSchema = TranslationsSchema(
  'name',
  'description'
)
export type PackageTranslations = z.infer<typeof PackageTranslationsSchema>

/**
 * PackageStatus
 */
export const [PackageStatuses, PackageStatusSchema, PackageStatusEnum] =
  EnumBuilder('draft', 'published', 'archived')
export type PackageStatus = z.infer<typeof PackageStatusSchema>

/**
 * PackagePayoutSchema
 */
export const PackagePayoutMax = 1_000_000
export const PackagePayoutSchema = DollarsSchema(PackagePayoutMax)
export type PackagePayout = z.infer<typeof PackagePayoutSchema>

/**
 * PackageDomainSelectModel
 */
export const PackageDomainSelectModelSchema = z.object({
  packageId: PackageIdSchema,
  packageUrn: PackageUrnSchema,
  packageSource: PackageSourceSchema,
  name: NameSchema,
  description: DescriptionSchema,
  translations: PackageTranslationsSchema.optional(),
  cost: PackagePayoutSchema,
  costPlusMargin: DollarsSchema(),
  status: PackageStatusSchema,
})

export type PackageDomainSelectModel = z.infer<
  typeof PackageDomainSelectModelSchema
>

/**
 * PackageDomainInsertModel
 */
export const PackageDomainInsertModelSchema =
  PackageDomainSelectModelSchema.omit({
    packageUrn: true,
    status: true, // Always inserted as draft
    costPlusMargin: true,
  }).partial({ packageId: true })

export type PackageDomainInsertModel = z.infer<
  typeof PackageDomainInsertModelSchema
>

/**
 * PackageDomainUpdateModel
 */
export const PackageDomainUpdateModelSchema =
  PackageDomainSelectModelSchema.pick({
    packageSource: true,
    name: true,
    description: true,
    translations: true,
    cost: true,
    status: true,
  }).partial()

export type PackageDomainUpdateModel = z.infer<
  typeof PackageDomainUpdateModelSchema
>

/**
 * PackageDomainSelectModelFilters
 */
export const PackageDomainSelectModelFiltersSchema = z
  .object({
    packageId: ModelFilterSchema(PackageIdSchema),
    packageUrn: ModelFilterSchema(PackageUrnSchema),
    packageSource: ModelFilterSchema(PackageSourceSchema),
    status: ModelFilterSchema(PackageStatusSchema),
  })
  .partial()

export type PackageDomainSelectModelFilters = z.infer<
  typeof PackageDomainSelectModelFiltersSchema
>

export const packagesByStatus =
  <Package extends { status: string }>(status: string) =>
  (pkg: Package): boolean =>
    z.string().safeParse(pkg.status).success && pkg.status === status
